import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { ChangeEventHandler, memo, useCallback, useDeferredValue, useMemo, useState } from 'react';
import { Input } from '../../components/inputs/Input';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { T, useT } from '../../translation/src';
import { SuperadminReferralOrganisationFieldsFragment, useSuperadminReferralsQuery, useSuperadminSetPartnerLinkMutation } from '../../types/graphqlTypes';

interface OrganisationRowProps {
  organisation: SuperadminReferralOrganisationFieldsFragment;
  // Which way the row moves: a linked organisation is unlinked (to the right), another one is linked (to the left).
  side: 'referred' | 'others';
  disabled: boolean;
  onMove: (organisationId: string) => void;
}
const OrganisationRow: React.FC<OrganisationRowProps> = ({ organisation, side, disabled, onMove }) => {
  const unlink = useT('unlink', { swc: true });
  const link = useT('link', { swc: true });
  const onClick = useCallback(() => onMove(organisation.id), [onMove, organisation.id]);
  const Arrow = side === 'referred' ? ArrowRightIcon : ArrowLeftIcon;
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={side === 'referred' ? unlink : link}
      className="flex items-center justify-center w-8 h-8 transition border rounded-full shrink-0 cursor-pointer border-gray-400 text-gray-600 hover:border-neon-green-300 hover:bg-neon-green-300 hover:text-black dark:border-gray-500 dark:text-gray-300 dark:hover:border-neon-green-300 dark:hover:bg-neon-green-300 dark:hover:text-black disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-400 disabled:hover:bg-transparent disabled:hover:text-gray-600"
    >
      <Arrow className="w-4 h-4" />
    </button>
  );
  return (
    <div className="flex items-center gap-2 py-2 text-sm border-b border-gray-200 last:border-b-0 dark:border-gray-700">
      {side === 'others' && button}
      <div className="flex flex-col min-w-0 grow">
        <span className="truncate">{organisation.name}</span>
        {/* Another partner's organisation: linking it here takes it away from that one. */}
        {side === 'others' && organisation.referredByName && (
          <span className="text-xs text-warning-yellow-700 dark:text-warning-yellow-500">
            <T _str="referred by {name}" args={{ name: organisation.referredByName }} />
          </span>
        )}
      </div>
      {side === 'referred' && button}
    </div>
  );
};

interface ColumnProps {
  title: React.ReactNode;
  count: React.ReactNode;
  empty: React.ReactNode;
  organisations: SuperadminReferralOrganisationFieldsFragment[];
  side: OrganisationRowProps['side'];
  disabled: boolean;
  onMove: OrganisationRowProps['onMove'];
  loading: boolean;
}
const Column: React.FC<ColumnProps> = ({ title, count, empty, organisations, side, disabled, onMove, loading }) => (
  <div className="flex flex-col min-w-0">
    <span className="mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">
      {title} ({count})
    </span>
    <div className="flex flex-col pr-2 overflow-y-auto h-80">
      {!loading && organisations.length === 0 && <div className="py-4 text-sm text-center text-gray-500">{empty}</div>}
      {organisations.map((organisation) => (
        <OrganisationRow key={organisation.id} organisation={organisation} side={side} disabled={disabled} onMove={onMove} />
      ))}
    </div>
  </div>
);

export interface ManageReferralsPanelProps {
  organisationId: string;
  organisationName: string;
  // Whether the panel is on screen: it asks the server only then.
  active: boolean;
}
// Two columns: the organisations linked to this one (acquired, managed or billed), and all the others. An arrow
// moves an organisation across: linked, it gets the relations a customer this partner made gets; unlinked, it drops
// every relation with this partner. The search narrows both columns.
const ManageReferralsPanel_: React.FC<ManageReferralsPanelProps> = ({ organisationId, organisationName, active }) => {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const { data, loading } = useSuperadminReferralsQuery({ variables: { organisationId, search: deferredSearch }, skip: !active, fetchPolicy: 'cache-and-network' });
  const [setPartnerLink, { loading: saving }] = useSuperadminSetPartnerLinkMutation({ refetchQueries: ['superadminReferrals'], awaitRefetchQueries: true });
  const { showToast } = useToastNotifications();
  const failed = useT('could not change the link', { swc: true });
  const searchPlaceholder = useT('search organisation', { swc: true });

  const referred = useMemo(() => {
    const all = data?.superadminReferrals.referred ?? [];
    const term = deferredSearch.trim().toLowerCase();
    // The linked ones are all here; the search filters them in place, as the server does the others.
    return term ? all.filter((organisation) => organisation.name.toLowerCase().includes(term)) : all;
  }, [data, deferredSearch]);
  const others = data?.superadminReferrals.others ?? [];
  const othersTotal = data?.superadminReferrals.othersTotal ?? 0;

  const onChangeSearch: ChangeEventHandler<HTMLInputElement> = useCallback((event) => setSearch(event.target.value), []);

  const move = useCallback(
    async (movedOrganisationId: string, linked: boolean) => {
      try {
        await setPartnerLink({ variables: { organisationId: movedOrganisationId, partnerOrganisationId: organisationId, linked } });
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [failed, organisationId, setPartnerLink, showToast],
  );
  const onLink = useCallback((movedOrganisationId: string) => move(movedOrganisationId, true), [move]);
  const onUnlink = useCallback((movedOrganisationId: string) => move(movedOrganisationId, false), [move]);

  return (
    <div className="flex flex-col gap-4">
      <Input value={search} onChange={onChangeSearch} placeholder={`${searchPlaceholder}...`} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Column title={<T _str="linked to {name}" args={{ name: organisationName }} />} count={referred.length} empty={<T _str="no linked organisations" swc />} organisations={referred} side="referred" disabled={saving} onMove={onUnlink} loading={loading} />
        <Column
          title={<T _str="other organisations" swc />}
          count={others.length < othersTotal ? <T _str="{shown} of {total}" args={{ shown: others.length, total: othersTotal }} /> : others.length}
          empty={<T _str="no other organisations" swc />}
          organisations={others}
          side="others"
          disabled={saving}
          onMove={onLink}
          loading={loading}
        />
      </div>
      {others.length < othersTotal && (
        <span className="text-xs text-gray-500">
          <T _str="Only the first {count} are shown: search to find the others." args={{ count: others.length }} />
        </span>
      )}
    </div>
  );
};
export const ManageReferralsPanel = memo(ManageReferralsPanel_);
